import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Link, usePage } from '@inertiajs/inertia-react';
import axios from 'axios';
import Layout from '../Shared/Layout';
import Imgix, { Background } from 'react-imgix';
import { useViewport } from '../Shared/Helpers';
import List from '../Components/List/List';
import ServiceCountdownComponent from '../../ServiceCountdownComponent';
import AppHead from '../Shared/AppHead';

const Channel = ({ channel, playlists, playlistVideos, uploadsOnly, dailyCountdownData }) => {
  const { imgixURL, isLoggedIn } = usePage().props;
  const { width } = useViewport();
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1000000);
  const [shouldLoadNextPage, setShouldLoadNextPage] = useState(
    playlists.next_page_url !== null
  );
  const [playlistsInState, setPlaylistsInState] = useState(playlists.data);
  const [playlistVideosInState, setPlaylistVideosInState] =
    useState(playlistVideos);
  const loadMorePlaylistsIntersect = useRef(null);
  const currentPageRef = useRef();
  currentPageRef.current = currentPage;
  const lastPageRef = useRef();
  lastPageRef.current = lastPage;
  const playlistsInStateRef = useRef();
  playlistsInStateRef.current = playlistsInState;
  const playlistVideosInStateRef = useRef();
  playlistVideosInStateRef.current = playlistVideosInState;

  const useIsInViewport = (ref) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    const observer = useMemo(
      () =>
        new IntersectionObserver((entries) =>
          entries.forEach(
            (entry) => entry.isIntersecting && loadMorePlaylists(),
            {
              rootMargin: '-150px 0px 0px 0px'
            }
          )
        ),
      []
    );

    useEffect(() => {
      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }, [ref, observer]);

    return isIntersecting;
  };
  const isInView = useIsInViewport(loadMorePlaylistsIntersect);
  const loadMorePlaylistVideos = (index, playlistId, currentPage, lastPage, callback) => {
    if (currentPage > lastPage) {
      return callback();
    }
    axios
      .get(`/on-demand/api/playlist/${playlistId}?page=${currentPage}`)
      .then(({ data }) => {
        setPlaylistVideosInState((playlistVideosInState) => {

          return playlistVideosInState.map(playlist => {
            let videos = [...playlist.videos.data];
            for (let videoIndex in data.data) {
              if (videos.findIndex(el => el.id === data.data[videoIndex].id) === -1) {
                videos.push(data.data[videoIndex]);
              }
            }
            return ({
              ...playlist,
              ...(playlist.id === playlistId && {
                videos: {
                  ...playlist.videos,
                  data: videos
                }
              })
            });
          });
        });
        return callback();
      });
  };
  const loadMorePlaylists = () => {
    // load more only if there's next page token
    if (currentPageRef.current > lastPageRef.current) {
      return;
    }
    axios
      .get(
        `/on-demand/api/channel/${channel.youtubeChannelId}?page=${
          currentPageRef.current + 1
        }`
      )
      .then(({ data: playlistDataFromApi }) => {
        setCurrentPage(currentPageRef.current + 1);
        setLastPage(playlistDataFromApi.playlists.last_page);
        setShouldLoadNextPage(
          playlistDataFromApi.playlists.next_page_url != null
        );

        for (let i = 0; i < playlistDataFromApi.playlists.data.length; i++) {
          let playlist = playlistDataFromApi.playlists.data[i];
          axios
            .get(`/on-demand/api/playlist/${playlist.id}`)
            .then(({ data }) => {
              setPlaylistVideosInState([
                ...playlistVideosInStateRef.current,
                {
                  id: playlist.id,
                  title: playlist.title,
                  videos: data
                }
              ]);
            });
        }

        setPlaylistsInState([
          ...playlistsInStateRef.current,
          ...playlistDataFromApi.playlists.data
        ]);
      })
      .catch((err) => {
        console.log(err);
      });
  };


  return (
    <>
      <AppHead title={channel.title} />

      <Background
        className='hero min-h-[15vh] max-h-[96px] md:min-h-[30vh] md:max-h-auto mb-4'
        src={`https://${imgixURL}/${channel.heroImage}`}
        imgixParams={
          width > 767
            ? { auto: 'format', fit: 'crop' }
            : {
              auto: 'format',
              fit: 'crop',
              crop: 'focalpoint',
              'fp-x': '.5',
              'fp-y': '.5',
              'fp-z': '2'
            }
        }
        sizes='100vw'
      />

      <div className='container' data-theme='jpm'>
        <div className='row'>
          <div className='col-12 md:col-1 lg:col-1 md:pr-0 lg:pr-0'>
            <div className='avatar'>
              <div className='w-12 md:w-full rounded-full'>
                <Imgix
                  className='w-full'
                  sizes='(min-width: 1280px) 72px, (min-width: 1024px) 72px, (min-width: 768px) 56px, (min-width: 640px) 48px, 48px'
                  src={`https://${imgixURL}/${channel.thumbnailIcon}`}
                  alt={channel.title}
                />
              </div>
            </div>
          </div>
          <div className='col-12 md:col-7 lg:col-8'>
            <h2 className='text-2xl font-semibold pb-2'>{channel.title}</h2>
            <p
              className='text-sm text-left text-white pb-3'
              dangerouslySetInnerHTML={{ __html: channel.description }}
            ></p>
          </div>
          <div className='col-12 md:col-4 lg:col-3'>
            {channel.buttonLink && channel.buttonLink.startsWith('http') ? (
              <a className='btn btn-outline btn-block' href={channel.buttonLink}>
                {channel.buttonLabel}
              </a>
            ) : (
                <Link className='btn btn-outline btn-block' href={channel.buttonLink}>
                    {channel.buttonLabel}
                </Link>
            )}
          </div>
        </div>

        {channel.slug == 'grc-online' && 
            <ServiceCountdownComponent
                isEnabled={dailyCountdownData?.isEnabled}
                nextServiceTimeText={dailyCountdownData?.nextServiceTimeText} 
                isServiceLiveNow={dailyCountdownData?.isServiceLiveNow} 
                initialDaysLeft={dailyCountdownData?.daysLeft} 
                initialHoursLeft={dailyCountdownData?.hoursLeft} 
                initialMinutesLeft={dailyCountdownData?.minutesLeft} 
                initialSecondsLeft={dailyCountdownData?.secondsLeft} /> 
        }

        <div className='col-12 mt-4 mb-5 border-b border-solid border-[#4F4F4F]'></div>
        {uploadsOnly ? (
          <div>
            <List
              isLoggedIn={isLoggedIn}
              elements={channelPlaylists.playlistVideos}
              totalElements={channelPlaylists.playlistVideos}
              viewportWidth={200}
            />
            <div className={`col-12 pt-2 md:pt-6 pb-6`}>
              <div className='w-full border-b border-solid border-[#7D7D7D]'></div>
            </div>
          </div>
        ) : (
          playlistsInState != null && playlistVideosInStateRef.current &&
          playlistsInStateRef.current.map((playlist, index) => {
            if (playlist != null) {
              let currentPlaylistWithVideos = playlistVideosInStateRef.current.find(el => el.id === playlist.id);
              return (
                <div key={index}>
                  {((currentPlaylistWithVideos && currentPlaylistWithVideos.videos.total > 0) &&
                    <div>
                      <h2 className='text-xl font-bold'>{currentPlaylistWithVideos.title}</h2>
                      <List
                        isLoggedIn={isLoggedIn}
                        elements={currentPlaylistWithVideos.videos.data}
                        playlistId={currentPlaylistWithVideos.id}
                        totalElements={currentPlaylistWithVideos.videos.total}
                        lastPage={currentPlaylistWithVideos.videos.last_page}
                        viewportWidth={width}
                        loadMore={(currentPage, callback) => loadMorePlaylistVideos(index, playlist.id, currentPage, currentPlaylistWithVideos.videos.last_page, callback)}
                      />
                      <div className={`col-12 pt-2 md:pt-6 pb-6`}>
                        <div className='w-full border-b border-solid border-[#7D7D7D]'></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
          })
        )}
        <span ref={loadMorePlaylistsIntersect} />
      </div>
    </>
  );
};

// Persistent layout Docs: https://inertiajs.com/pages#persistent-layouts
Channel.layout = (page) => (
  <Layout title='Channel' children={page} data-theme='jpm' />
);

export default Channel;
